import classNames from 'classnames';
import { isNumber } from 'lodash';
import { memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

export type ToastColor = 'black' | 'red' | 'green';

export type ToastPlacement = 'top' | 'bottom';

const TOAST_COLOR_CLASS_NAMES: { [key in ToastColor]: string } = {
  black: 'ads:bg-inverse bg-pigeon-700 ads:text-inverse text-white',
  green: 'ads:bg-success bg-green-4 ads:text-primary text-green-12',
  red: 'ads:bg-error bg-flamingo-600 ads:text-inverse text-white',
};

export type ToastItemProps = {
  children: ReactNode;
  color?: ToastColor;
  onDismiss: () => void;

  /** This is a component that can show in front of the message.. */
  prefix?: ReactNode;
  /** This is a component that can show in back of the message.. */
  suffix?: ReactNode;
  timeBeforeAutoDismiss?: number | null;
  /**
   * Controls whether the assistive technology should read immediately ("assertive") or wait until the
   * user is idle ("polite"). Think to yourself: "If this toast wasn't visible, would a user be confused?"
   * If the answer to that question is "yes", the type should probably be "assertive".
   *
   * "assertive" makes the toast have `role="alert"` and "polite" makes the toast have `role="status"`
   *
   * @default 'polite'
   * @example Success notifications can be polite, and server errors from a form submission should be alert.
   * @see https://reach.tech/alert#alert-type
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_status_role
   */
  type?: 'assertive' | 'polite';
  withCloseButton?: boolean;
};

export const ToastItem = memo(
  ({
    children,
    color = 'black',
    onDismiss,
    prefix,
    suffix,
    timeBeforeAutoDismiss,
    type,
    withCloseButton,
  }: ToastItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const handlePointerEnter = useCallback(() => {
      setIsHovered(true);
    }, []);

    const handlePointerLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: dismiss as dependency causes issue with resetting the timeout
    useEffect(() => {
      let timer = 0;

      if (isNumber(timeBeforeAutoDismiss) && !isHovered) {
        timer = window.setTimeout(onDismiss, timeBeforeAutoDismiss);
      }

      return () => clearTimeout(timer);
    }, [isHovered, timeBeforeAutoDismiss]);

    return (
      <div
        className={classNames(
          'relative my-1 flex min-h-12 items-center gap-3 overflow-hidden rounded-8 px-4 py-3 shadow-popover',
          TOAST_COLOR_CLASS_NAMES[color],
        )}
        data-testid="TOAST_ITEM"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        role={type === 'assertive' ? 'alert' : 'status'}
      >
        {prefix ? (
          <div className="shrink-0" data-testid="TOAST_PREFIX">
            {prefix}
          </div>
        ) : null}

        <span className="font-medium text-14" data-testid="TOAST_TEXT">
          {children}
        </span>

        {suffix ? (
          <div className="shrink-0" data-testid="TOAST_PREFIX">
            {suffix}
          </div>
        ) : null}

        {withCloseButton && (
          <IconButton
            appearance="ghost"
            // Icon colors are emitted after text colors, so this beats ghost gray's ads:text-secondary.
            // Inverse is always white, which disappears on the light success surface.
            className={
              color === 'green'
                ? 'shrink-0 ads:text-icon-emphasis/80 text-green-12/80'
                : 'shrink-0 ads:text-icon-inverse/80 text-white/80'
            }
            color="gray"
            data-testid="TOAST_CLOSE_BUTTON"
            icon={XIcon}
            label="Close toast"
            onClick={onDismiss}
            size="small"
          />
        )}
      </div>
    );
  },
);

ToastItem.displayName = 'ToastItem';
